import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import BrandMark from "@/components/BrandMark";
import { ArrowLeft } from "lucide-react";

const UPDATED = "10 de julio de 2026";
const H = "font-display font-bold text-xl mt-8 mb-3";
const P = "text-sm text-[var(--text-dim)] leading-relaxed mb-3";
const UL = "list-disc pl-5 space-y-1.5 text-sm text-[var(--text-dim)] mb-4";

function Shell({ title, children }) {
  useEffect(() => {
    document.title = `${title} · Agent Company OS`;
    window.scrollTo(0, 0);
  }, [title]);
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="border-b border-[var(--border)]">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandMark className="w-8 h-8" />
            <div className="font-display font-black text-[15px] tracking-tight">AGENT COMPANY OS</div>
          </Link>
          <Link to="/" className="text-sm text-[var(--text-dim)] hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Inicio
          </Link>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-2">{title}</h1>
        <p className="label-mono mb-8">Última actualización: {UPDATED}</p>
        {children}
        <div className="mt-12 pt-6 border-t border-[var(--border)] flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <Link to="/privacidad" className="text-[var(--text-dim)] hover:text-[var(--accent)]">Privacidad</Link>
          <Link to="/cookies" className="text-[var(--text-dim)] hover:text-[var(--accent)]">Cookies</Link>
          <Link to="/terminos" className="text-[var(--text-dim)] hover:text-[var(--accent)]">Términos</Link>
        </div>
      </div>
    </div>
  );
}

function Titular() {
  return (
    <p className={P}>
      <strong className="text-white">Titular:</strong> Eduardo Sánchez Velayos · <strong className="text-white">NIF:</strong> 43208236E ·{" "}
      Carrer Prat de la Riba 11, Palma (Illes Balears) · <strong className="text-white">Contacto:</strong> edusanchez98.98@gmail.com
    </p>
  );
}

function Privacidad() {
  return (
    <Shell title="Política de Privacidad">
      <h2 className={H}>1. Responsable del tratamiento</h2>
      <Titular />
      <h2 className={H}>2. Qué datos tratamos</h2>
      <ul className={UL}>
        <li><strong>De registro:</strong> nombre, email, empresa, contraseña (cifrada).</li>
        <li><strong>De uso:</strong> mensajes con los agentes, documentos que subes, datos de tu negocio, registros de actividad y datos técnicos (IP, navegador).</li>
        <li><strong>De pago:</strong> gestionados por Stripe; no almacenamos los datos completos de la tarjeta.</li>
        <li><strong>Claves de API de IA (BYOK):</strong> almacenadas cifradas y usadas solo para prestarte el servicio.</li>
        <li><strong>De contacto/lead:</strong> los datos que envías en el formulario de servicio (nombre, negocio, contacto), para responderte.</li>
      </ul>
      <h2 className={H}>3. Finalidad y base jurídica</h2>
      <ul className={UL}>
        <li>Prestar y mantener el servicio — ejecución del contrato.</li>
        <li>Facturación y gestión de la suscripción — contrato / obligación legal.</li>
        <li>Soporte y atención de solicitudes — contrato / interés legítimo.</li>
        <li>Seguridad y prevención de abusos — interés legítimo.</li>
        <li>Medición publicitaria y comunicaciones comerciales — consentimiento.</li>
      </ul>
      <h2 className={H}>4. Procesado por IA</h2>
      <p className={P}>
        Cuando usas los agentes, el texto de tu consulta (y, en su caso, fragmentos de tus documentos) se envía al
        proveedor de IA correspondiente para generar la respuesta. Si aportas tu propia clave (BYOK), el tratamiento
        se realiza bajo tu cuenta con dicho proveedor y sujeto a sus condiciones.
      </p>
      <h2 className={H}>5. Encargados y terceros</h2>
      <p className={P}>
        Usamos proveedores que tratan datos por cuenta nuestra bajo contrato: alojamiento (Hetzner, UE), pagos
        (Stripe), proveedores de IA (Anthropic, OpenAI o Google, según tu configuración) y medición (Google Ads,
        solo con tu consentimiento). No vendemos tus datos. Algunas transferencias fuera del EEE pueden estar
        cubiertas por Cláusulas Contractuales Tipo.
      </p>
      <h2 className={H}>6. Conservación</h2>
      <p className={P}>
        Conservamos los datos mientras la cuenta esté activa y, tras la baja, el tiempo necesario para cumplir
        obligaciones legales o resolver reclamaciones. Después se suprimen o anonimizan.
      </p>
      <h2 className={H}>7. Tus derechos</h2>
      <p className={P}>
        Puedes ejercer los derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad
        escribiendo a edusanchez98.98@gmail.com, acreditando tu identidad. También puedes reclamar ante la Agencia
        Española de Protección de Datos (AEPD) — www.aepd.es.
      </p>
      <h2 className={H}>8. Seguridad</h2>
      <p className={P}>
        Aplicamos medidas técnicas y organizativas: cifrado de contraseñas (bcrypt) y de las claves de IA (Fernet),
        aislamiento por cuenta, control de accesos, registros de auditoría y comunicaciones cifradas (HTTPS).
      </p>
      <h2 className={H}>9. Menores</h2>
      <p className={P}>El servicio no está dirigido a menores de edad. No recabamos conscientemente datos de menores.</p>
      <h2 className={H}>10. Cambios</h2>
      <p className={P}>Podemos actualizar esta política; publicaremos la versión vigente con su fecha.</p>
    </Shell>
  );
}

function Cookies() {
  return (
    <Shell title="Política de Cookies">
      <h2 className={H}>1. Qué son las cookies</h2>
      <p className={P}>
        Las cookies y tecnologías similares (p.ej. <code>localStorage</code>) son pequeños archivos que se guardan
        en tu dispositivo para que la aplicación funcione, recuerde tu sesión y, si lo autorizas, medir la
        publicidad.
      </p>
      <h2 className={H}>2. Cookies y almacenamiento que usamos</h2>
      <ul className={UL}>
        <li><strong>aios_token / aios_user / aios_onboarded</strong> — técnicas (necesarias): mantener la sesión y tu configuración.</li>
        <li><strong>aios_cookie_consent</strong> — técnica: recordar tu elección sobre las cookies.</li>
        <li><strong>Google Ads (_gcl_*, y similares)</strong> — publicidad/medición: solo se activan si pulsas «Aceptar» en el banner. Miden si un anuncio ha traído un contacto. Gestionadas por Google.</li>
      </ul>
      <h2 className={H}>3. Consentimiento</h2>
      <p className={P}>
        Las cookies técnicas son imprescindibles y siempre están activas. Las de publicidad/medición están
        <strong className="text-white"> desactivadas por defecto</strong> y solo se cargan si las aceptas en el banner de cookies. Puedes
        cambiar tu elección borrando el almacenamiento del navegador para que el banner vuelva a aparecer.
      </p>
      <h2 className={H}>4. Cambios</h2>
      <p className={P}>Actualizaremos esta política si cambian las tecnologías utilizadas.</p>
      <p className={P}>Consulta también nuestra <Link to="/privacidad" className="text-[var(--accent)] hover:underline">Política de Privacidad</Link>.</p>
    </Shell>
  );
}

function Terminos() {
  return (
    <Shell title="Términos y Condiciones">
      <Titular />
      <p className={P}>Servicio: Agent Company OS (en adelante, «la Plataforma»), accesible en https://aicompany.beautyos.es.</p>
      <h2 className={H}>1. Objeto</h2>
      <p className={P}>
        Estas condiciones regulan el acceso y uso de la Plataforma, un software en la nube (SaaS) que proporciona
        asistentes de inteligencia artificial («agentes») para tareas de apoyo empresarial. El uso de la Plataforma
        implica la aceptación plena de estas condiciones.
      </p>
      <h2 className={H}>2. Descripción del servicio</h2>
      <p className={P}>
        La Plataforma permite chatear con agentes de IA, subir documentos y gestionar información del negocio. Los
        agentes ofrecen apoyo y generan texto; no sustituyen el criterio profesional del Usuario, que es responsable
        de revisar y validar cualquier resultado antes de usarlo.
      </p>
      <h2 className={H}>3. Cuenta y registro</h2>
      <p className={P}>
        El Usuario debe registrarse con datos veraces, ser mayor de edad y es responsable de la confidencialidad de
        sus credenciales y de la actividad de su cuenta.
      </p>
      <h2 className={H}>4. Planes, precios y pago</h2>
      <p className={P}>
        El acceso se ofrece por planes de suscripción con las características y precios publicados en la sección de
        precios. Los pagos se procesan a través de Stripe. Salvo indicación en contrario, las suscripciones se
        renuevan automáticamente hasta su cancelación, que surte efecto al final del periodo en curso.
      </p>
      <h2 className={H}>5. Claves de IA del Usuario (BYOK)</h2>
      <p className={P}>
        Ciertos planes requieren que el Usuario aporte su propia clave de API de un proveedor de IA. El consumo y el
        coste de esa API corren por cuenta del Usuario. La Plataforma almacena dichas claves cifradas y las usa
        únicamente para prestar el servicio.
      </p>
      <h2 className={H}>6. Uso aceptable</h2>
      <p className={P}>
        El Usuario se compromete a no usar la Plataforma para fines ilícitos ni para generar contenido que infrinja
        derechos de terceros o la normativa aplicable, ni a vulnerar la seguridad o sobrecargar el servicio.
      </p>
      <h2 className={H}>7. Contenido del Usuario</h2>
      <p className={P}>
        El Usuario conserva la titularidad de sus datos y documentos, y concede una licencia limitada para tratarlos
        con el único fin de prestar el servicio, garantizando que dispone de los derechos necesarios.
      </p>
      <h2 className={H}>8. Propiedad intelectual</h2>
      <p className={P}>
        El software, la marca «Agent Company OS», el diseño y los agentes predefinidos son titularidad de Eduardo
        Sánchez Velayos o de sus licenciantes. No se cede ningún derecho más allá del uso del servicio.
      </p>
      <h2 className={H}>9. Disponibilidad y cambios</h2>
      <p className={P}>
        La Plataforma se presta «tal cual» y «según disponibilidad». Podremos modificar, suspender o discontinuar
        funciones. Podemos actualizar estas condiciones; el uso continuado implicará su aceptación.
      </p>
      <h2 className={H}>10. Limitación de responsabilidad</h2>
      <p className={P}>
        En la máxima medida permitida por la ley, el titular no será responsable de decisiones tomadas por el Usuario
        en base a los resultados de los agentes, ni de daños indirectos o lucro cesante. La IA puede cometer errores;
        el Usuario debe verificar la información. La responsabilidad total se limita al importe abonado en los 12
        meses anteriores.
      </p>
      <h2 className={H}>11. Terminación</h2>
      <p className={P}>
        Podemos suspender o cancelar cuentas que incumplan estas condiciones. El Usuario puede darse de baja en
        cualquier momento. Tras la baja, los datos se tratarán conforme a la Política de Privacidad.
      </p>
      <h2 className={H}>12. Protección de datos</h2>
      <p className={P}>
        El tratamiento de datos personales se rige por la{" "}
        <Link to="/privacidad" className="text-[var(--accent)] hover:underline">Política de Privacidad</Link>.
      </p>
      <h2 className={H}>13. Ley aplicable y jurisdicción</h2>
      <p className={P}>
        Estas condiciones se rigen por la legislación española. Salvo normativa imperativa de consumo, las partes se
        someten a los juzgados de Palma.
      </p>
    </Shell>
  );
}

export default function Legal({ doc }) {
  if (doc === "cookies") return <Cookies />;
  if (doc === "terminos") return <Terminos />;
  return <Privacidad />;
}
